import { useLayoutEffect, useRef, useState } from "react";

export const RecoveryPassphraseActions = ({
  passphrase,
  accountId,
  view,
  disabled,
  generationDisabled,
  onGenerate,
}: {
  passphrase: string;
  accountId?: string;
  view: "create" | "export";
  disabled: boolean;
  generationDisabled: boolean;
  onGenerate: () => void;
}) => {
  const [copyStatus, setCopyStatus] = useState<
    "idle" | "copying" | "copied" | "failed"
  >("idle");
  const copyAttempt = useRef(0);

  useLayoutEffect(() => {
    copyAttempt.current += 1;
    setCopyStatus("idle");
    return () => {
      // Ignore clipboard results from another passphrase/account/view or a closed panel.
      copyAttempt.current += 1;
    };
  }, [passphrase, accountId, view, disabled]);

  const copy = async () => {
    if (disabled || !passphrase || copyStatus === "copying") return;
    const attempt = ++copyAttempt.current;
    setCopyStatus("copying");
    try {
      if (typeof navigator.clipboard?.writeText !== "function") {
        throw new Error("Clipboard unavailable");
      }
      await navigator.clipboard.writeText(passphrase);
      if (attempt === copyAttempt.current) setCopyStatus("copied");
    } catch {
      if (attempt === copyAttempt.current) setCopyStatus("failed");
    }
  };

  return (
    <div className="private-mode-access__passphrase-tools">
      <div className="private-mode-access__passphrase-actions">
        <button
          type="button"
          className="transparent private-mode-access__link"
          disabled={disabled || generationDisabled}
          onClick={() => {
            if (!disabled && !generationDisabled) onGenerate();
          }}>
          Generate passphrase
        </button>
        <button
          type="button"
          className="transparent private-mode-access__link"
          disabled={disabled || !passphrase || copyStatus === "copying"}
          aria-label={
            copyStatus === "copied" ? "Passphrase copied" : "Copy passphrase"
          }
          aria-live="polite"
          onClick={copy}>
          {copyStatus === "copied"
            ? "Copied"
            : copyStatus === "copying"
              ? "Copying..."
              : "Copy"}
        </button>
      </div>
      {copyStatus === "failed" && (
        <p className="private-mode-access__hint" role="status">
          Show the passphrase and copy it manually.
        </p>
      )}
    </div>
  );
};
